<template>
  <div v-if="searchable" class="px-2 pb-2 mb-2">
    <CustomInput
      format="text"
      v-model="localSearchValue"
      :placeholder="`Search ${filterName.toLowerCase()}`"
      type="primary"
      :icon="imageNames.SEARCH_VARIANT1"
      size="size-4"
      alt="search_icon"
      @click.stop=""
      @keydown.stop="$event.stopPropagation()"
      @keydown.enter="allowCustomInput ? handleAddCustomValue() : null"
    />
  </div>
  <div class="dropdown_content border_top">
    <template v-if="isLoading">
      <div class="px-2 py-3">
        <SkeletonLoader page="manage_columns" />
      </div>
    </template>
    <template v-else-if="filteredOptions.length > 0">
      <div ref="optionsContainer" class="px-2 max-h-72 overflow-auto py-3">
        <div
          v-for="(option, index) in filteredOptions"
          :key="getOptionKey(option, index)"
          :class="[
            'px-2 py-2 cursor-pointer dropdown_item',
            { selected_option: option.selected },
          ]"
          @click="() => handleOptionClick(option)"
        >
          <div class="flex items-center gap-2 truncate">
            <ThemeImage
              v-if="option.selected"
              :icon="imageNames.TICK_GREY"
              alt="tick_icon"
              class="size-4"
            />
            <div v-else class="size-4 shrink-0" />

            <RemovedUserIcon
              v-if="showInitials && option.isDeleted && option.id !== currentUserId && option.id !== -1"
              :size="32"
            />
            <ProfileIcon
              :data="getOptionData(option)"
              v-if="showInitials && !option.isDeleted && option.id !== currentUserId && option.id !== -1"
            />

            <a-tooltip
              placement="top"
              :title="getTooltipTitle('fol_' + getOptionKey(option, index), option.name || '')"
              :overlay-style="{ zIndex: 10000 }"
            >
              <div
                class="truncate min-w-0"
                :ref="(el) => setContainerRef(el, 'fol_' + getOptionKey(option, index))"
              >
                <span :ref="(el) => setTextRef(el, 'fol_' + getOptionKey(option, index))">
                  <RemovedUserLabel
                    :name="option.name"
                    :is-deleted="option.isDeleted"
                  />
                </span>
              </div>
            </a-tooltip>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        class="px-2 pt-3 h-64 flex flex-col items-center justify-center w-full"
      >
        <ThemeImage
          :icon="imageNames.EMPTY_SCREEN_GREY"
          alt="empty_icon"
          class="size-16"
        />
        <span class="text-sm secondary_color">No results found!</span>
      </div>
    </template>
  </div>

  <div v-if="isDateFilter" class="flex flex-col border_top px-3 pt-2">
    <div
      class="dropdown_item flex items-center !justify-between w-full flex-row gap-2 rounded-lg cursor-pointer"
      @click.stop="handleCustomDateClick"
    >
      <div class="text_color text-sm font-normal">Custom date</div>
      <ThemeImage
        :icon="imageNames.RIGHT_ARROW_SMALL_GREY"
        alt="right_arrow"
        class="size-4"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  dateFilterTypes,
  searchableFilterIds,
  currentUserId,
} from "@/src/constants/crmConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import RemovedUserIcon from "@/src/components/RemovedUserIcon.vue";
import RemovedUserLabel from "@/src/components/RemovedUserLabel.vue";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const getOptionData = (option) => {
  if (!option?.id || option.id < 0) return option;
  const found = storeUsers.value.find((u) => String(u.id) === String(option.id));
  return found || option;
};

const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();
// Reactive counter to trigger re-evaluation of shouldShowTooltip after refs are set
const tooltipRevision = ref(0);

const props = defineProps({
  filterId: { type: String, required: true },
  filterName: { type: String, required: true },
  options: { type: Array, required: true },
  searchable: { type: Boolean, default: false },
  showInitials: { type: Boolean, default: false },
  isDate: { type: Boolean, default: false },
  hasMoreOptions: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  allowCustomInput: { type: Boolean, default: false },
});

const emit = defineEmits([
  "select-option",
  "search",
  "custom-date",
  "load-more",
]);
const localSearchValue = ref("");
const optionsContainer = ref(null);
let observer = null;
let bottomElement = null;


const isDateFilter = computed(() => {
  return props.isDate || dateFilterTypes.includes(props.filterId);
});

const handleCustomDateClick = () => {
  emit("custom-date", props.filterId);
};

const handleOptionClick = (option) => {
  localSearchValue.value = "";
  emit("select-option", option);
};

const handleAddCustomValue = () => {
  const customValue = localSearchValue.value.trim();
  if (customValue) {
    const existingOption = props.options.find(
      (option) => option.name.toLowerCase() === customValue.toLowerCase(),
    );

    if (existingOption) {
      localSearchValue.value = "";
      emit("select-option", existingOption);
    } else {
      const customOption = {
        id: customValue.toLowerCase().replace(/\s+/g, "_"),
        name: customValue,
        selected: false,
      };
      localSearchValue.value = "";
      emit("select-option", customOption);
    }
  }
};

const filteredOptions = computed(() => {
  if (!props.options || !Array.isArray(props.options)) return [];
  let filteredList = props.options;

  if (
    !searchableFilterIds.includes(props.filterId) &&
    localSearchValue.value &&
    props.searchable
  ) {
    filteredList = props.options.filter(
      (option) =>
        option.name &&
        option.name
          .toLowerCase()
          .includes(localSearchValue.value.toLowerCase()),
    );
  }

  return filteredList;
});

// After options render, bump revision so tooltip titles re-evaluate with populated refs
watch(filteredOptions, () => {
  nextTick(() => {
    tooltipRevision.value++;
  });
}, { immediate: true });

const getTooltipTitle = (key, label) => {
  // Reference tooltipRevision to make this reactive - re-evaluated after refs are set
  void tooltipRevision.value;
  return shouldShowTooltip(key) ? label : "";
};

const getOptionKey = (option, index) => {
  const optionId =
    option && option.id !== undefined && option.id !== null
      ? option.id
      : `${index}`;
  return `${props.filterId}_${optionId}`;
};

const setupObserver = async () => {
  await nextTick();

  if (observer && bottomElement) {
    observer.unobserve(bottomElement);
  }

  if (!optionsContainer.value) return;

  const lastChild = optionsContainer.value.lastElementChild;
  if (!lastChild) return;

  if (!observer) {
    observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting && props.hasMoreOptions) {
          emit("load-more", props.filterId);
        }
      },
      {
        root: optionsContainer.value,
        threshold: 0.1,
      },
    );
  }

  bottomElement = lastChild;
  observer.observe(bottomElement);
};

watch(localSearchValue, (newValue) => {
  emit("search", props.filterId, newValue);
});

watch(
  () => props.options,
  async () => {
    if (props.options.length > 0 && props.hasMoreOptions) {
      await setupObserver();
    }
  },
  { deep: true },
);

onMounted(() => {
  localSearchValue.value = "";
  if (props.options.length > 0 && props.hasMoreOptions) {
    setupObserver();
  }
  // Ensure users/users is loaded so getOptionData can resolve correct avatar_color
  if (props.showInitials && storeUsers.value.length === 0) {
    store.dispatch("users/fetchUsers");
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style scoped>
.dropdown_content {
  max-height: 300px;
}

.dropdown_item {
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  transform: translateZ(0);
}

.dropdown_item:hover {
  background-color: var(--white-opacity-10);
}

.selected_option {
  background-color: var(--white-opacity-8);
  transition: background-color 0.15s ease;
}
</style>